import { Link } from "wouter";
import { Phone, Mail, MapPin, Clock, Shield, Star } from "lucide-react";

const LOGO_URL =
  "https://files.manuscdn.com/user_upload_by_module/session_file/310519663448310112/KkdVDzUbtvERWOCX.webp";

export default function Footer() {
  return (
    <footer className="bg-[oklch(0.16_0.05_255)] text-white">
      {/* Main footer content */}
      <div className="container mx-auto px-4 lg:px-8 py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand column */}
          <div className="lg:col-span-1">
            <img
              src={LOGO_URL}
              alt="Pest Control RAF SERV LTD"
              className="h-14 w-auto object-contain mb-4"
              width="140"
              height="56"
              loading="lazy"
            />
            <p className="text-white/70 text-sm leading-relaxed mb-4">
              London's trusted pest control specialists. Professional, fast, and
              fully insured service across all London postcodes.
            </p>
            <div className="flex items-center gap-1 mb-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <Star
                  key={i}
                  className="w-4 h-4 fill-[oklch(0.72_0.18_65)] text-[oklch(0.72_0.18_65)]"
                />
              ))}
              <span className="text-white/80 text-sm ml-1">
                Top Rated on Google
              </span>
            </div>
            <div className="flex items-center gap-2 text-white/80 text-xs">
              <Shield className="w-4 h-4 text-[oklch(0.72_0.18_65)]" />
              Fully Insured &amp; BPCA Certified
            </div>
            {/* Google Guaranteed Badge */}
            <a
              href="https://share.google/4FC6tIx8GCbnuMXvC"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white rounded-lg px-3 py-2 mt-3 hover:shadow-md transition-all border border-green-200"
            >
              <div className="flex items-center justify-center w-7 h-7 rounded-full bg-green-500 shrink-0">
                <svg viewBox="0 0 24 24" className="w-4 h-4 fill-white" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                </svg>
              </div>
              <div>
                <div className="flex items-center gap-0.5">
                  <span className="text-[#4285F4] font-bold text-xs">G</span>
                  <span className="text-[#EA4335] font-bold text-xs">o</span>
                  <span className="text-[#FBBC05] font-bold text-xs">o</span>
                  <span className="text-[#4285F4] font-bold text-xs">g</span>
                  <span className="text-[#34A853] font-bold text-xs">l</span>
                  <span className="text-[#EA4335] font-bold text-xs">e</span>
                  <span className="text-green-600 font-bold text-xs ml-1">Guaranteed</span>
                </div>
                <p className="text-gray-500 text-xs">Verified business</p>
              </div>
            </a>
          </div>

          {/* Services column */}
          <div>
            <h3 className="text-white font-semibold text-base mb-4">
              Our Services
            </h3>
            <ul className="space-y-2">
              {[
                ["Rat Control", "/services/rats"],
                ["Mice Control", "/services/mice"],
                ["Cockroach Treatment", "/services/cockroaches"],
                ["Wasp Nest Removal", "/services/wasps"],
                ["Bed Bug Treatment", "/services/bedbugs"],
                ["Ant Control", "/services/ants"],
                ["Flea Treatment", "/services/fleas"],
                ["All Services", "/services"],
              ].map(([label, href]) => (
                <li key={href}>
                  <Link
                    href={href}
                    className="text-white/70 hover:text-[oklch(0.72_0.18_65)] text-sm transition-colors"
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick links column */}
          <div>
            <h3 className="text-white font-semibold text-base mb-4">
              Quick Links
            </h3>
            <ul className="space-y-2">
              {[
                ["Book Online", "/book"],
                ["Get a Quote", "/quote"],
                ["Emergency Callout", "/book?emergency=true"],
                ["Commercial Services", "/commercial"],
                ["Gallery", "/gallery"],
                ["Blog & Advice", "/blog"],
                ["Areas We Cover", "/locations"],
                ["Pest Control Near Me", "/pest-control-near-me"],
                ["FAQ — 250+ Answers", "/faq"],
                ["Pest ID Guide", "/pest-guide"],
                ["About Us", "/about"],
                ["Contact", "/contact"],
                ["My Account", "/account"],
              ].map(([label, href]) => (
                <li key={href}>
                  <Link
                    href={href}
                    className="text-white/70 hover:text-[oklch(0.72_0.18_65)] text-sm transition-colors"
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact column */}
          <div>
            <h3 className="text-white font-semibold text-base mb-4">
              Contact Us
            </h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <Phone className="w-4 h-4 text-[oklch(0.72_0.18_65)] mt-0.5 shrink-0" />
                <div>
                  <a
                    href="tel:07586072087" onClick={() => { if (typeof gtag_report_conversion !== "undefined") gtag_report_conversion("tel:07586072087"); }}
                    className="text-white/90 hover:text-[oklch(0.72_0.18_65)] text-sm font-medium transition-colors block"
                  >
                    07586 072087
                  </a>

                </div>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-4 h-4 text-[oklch(0.72_0.18_65)] mt-0.5 shrink-0" />
                <a
                  href="mailto:pestcontrol2122@gmail.com"
                  className="text-white/70 hover:text-[oklch(0.72_0.18_65)] text-sm transition-colors"
                >
                  pestcontrol2122@gmail.com
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-[oklch(0.72_0.18_65)] mt-0.5 shrink-0" />
                <address className="text-white/70 text-sm not-italic leading-relaxed">
                  Office No. 1
                  <br />
                  Rockware Business Centre
                  <br />
                  5 Rockware Ave, Greenford
                  <br />
                  UB6 0AA
                </address>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="w-4 h-4 text-[oklch(0.72_0.18_65)] mt-0.5 shrink-0" />
                <div className="text-white/70 text-sm">
                  <span className="text-[oklch(0.72_0.18_65)] font-semibold">
                    24/7
                  </span>{" "}
                  Emergency Service
                  <br />
                  Mon–Sun, all hours
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="container mx-auto px-4 lg:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-white/75 text-xs text-center sm:text-left">
            © {new Date().getFullYear()} Pest Control RAF SERV LTD. All rights
            reserved. Registered in England &amp; Wales.
          </p>
          <div className="flex items-center gap-4">
            <Link
              href="/privacy"
              className="text-white/75 hover:text-white text-xs transition-colors"
            >
              Privacy Policy
            </Link>
            <Link
              href="/terms"
              className="text-white/75 hover:text-white text-xs transition-colors"
            >
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
